import { useProject } from "@/features/projects/hooks";
import { ProjectAction } from "@/features/projects/project-action";
import { Plus } from "lucide-react";
import { Box } from "@/components/ui/box";
import { Button, ButtonLink } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page";
import { EmptyState, QueryState } from "@/components/ui/states";
import { useUrlState } from "@/lib/use-url-state";
import { CompareButton, useCompareSelection } from "@/features/compare/use-compare-selection";
import { applyFilters, ExperimentsToolbar, useExperimentFilters } from "./experiments-filters";
import { ExperimentsTable } from "./experiments-table";
import { RecordedPanel } from "./recorded/recorded-panel";
import { useExperiments } from "./hooks";
import { ArchiveEmpty, ArchiveTabs } from "@/features/archive/archived";
import { useArchiveTabs } from "@/features/archive/hooks";

const newButton = (
  <ProjectAction permission="experiments:write">
    <ButtonLink variant="primary" to="/experiments/new">
      New experiment
    </ButtonLink>
  </ProjectAction>
);

export function ExperimentsPage() {
  const { can } = useProject();
  const url = useUrlState();
  const filterState = useExperimentFilters();
  const tabs = useArchiveTabs("experiment", useExperiments());
  const experiments = tabs.query;
  const archived = tabs.counts.archived ?? 0;
  const all = experiments.data ?? [];
  const rows = applyFilters(all, filterState.filters);
  const { selection, picked } = useCompareSelection(all);

  return (
    <>
      <PageHeader
        title="Experiments"
        subtitle="Every experiment across all agents, newest first."
        actions={
          <>
            <ProjectAction permission="experiments:write">
              <Button onClick={() => url.set({ import: 1 })}>Score recorded outputs</Button>
            </ProjectAction>
            <ProjectAction permission="experiments:write">
              <ButtonLink variant="primary" to="/experiments/new">
                <Plus size={14} strokeWidth={2.2} aria-hidden />
                New experiment
              </ButtonLink>
            </ProjectAction>
          </>
        }
      />
      <ArchiveTabs kind="experiment" {...tabs.counts} />
      <div className="flex flex-wrap items-center gap-2">
        <ExperimentsToolbar experiments={all} state={filterState} />
        {filterState.active && experiments.data && (
          <span className="font-mono text-xs text-muted">
            {rows.length} of {all.length}
          </span>
        )}
        <span className="grow" />
        <CompareButton picked={picked} />
      </div>
      {can("experiments:write") && url.get("import") === "1" && (
        <RecordedPanel onClose={() => url.set({ import: null })} />
      )}
      <Box>
        <QueryState query={experiments} what="experiments">
          {rows.length ? (
            <ExperimentsTable experiments={rows} selection={selection} actions />
          ) : filterState.active ? (
            <EmptyState
              title="No experiments match these filters"
              action={<Button onClick={filterState.clear}>Clear filters</Button>}
            >
              Try a different name, or widen the agent, dataset, status or evaluator.
            </EmptyState>
          ) : tabs.view === "archived" || archived > 0 ? (
            <ArchiveEmpty kind="experiment" view={tabs.view} archived={archived} create={newButton} />
          ) : (
            <EmptyState title="No experiments yet" action={newButton}>
              Pick a connection and an agent, a dataset and evaluators, and run it.
            </EmptyState>
          )}
        </QueryState>
      </Box>
    </>
  );
}
